import { Controller } from "@hotwired/stimulus"

// Community composer: collapsed pill → expanded form with text / image / link
// / poll panels. Each chip switches the active panel and updates the
// `post_type` hidden field so the controller persists the right kind of post.
export default class extends Controller {
  static targets = [
    "trigger", "form", "bodyInput", "titleInput", "postType",
    "imagePanel", "linkPanel", "pollPanel",
    "imageInput", "imagePreview", "linkInput",
    "pollOptions"
  ]

  expand(event) {
    event?.preventDefault()
    this._show()
    requestAnimationFrame(() => this.bodyInputTarget?.focus())
  }

  expandWithImage(event) {
    event?.preventDefault()
    event?.stopPropagation()
    this._show()
    this._switchPanel("image")
  }

  expandWithLink(event) {
    event?.preventDefault()
    event?.stopPropagation()
    this._show()
    this._switchPanel("link")
    requestAnimationFrame(() => this.linkInputTarget?.focus())
  }

  expandWithPoll(event) {
    event?.preventDefault()
    event?.stopPropagation()
    this._show()
    this._switchPanel("poll")
  }

  collapse(event) {
    event?.preventDefault()
    if (this.hasFormTarget) this.formTarget.hidden = true
    if (this.hasTriggerTarget) this.triggerTarget.hidden = false
    this._switchPanel("text")
    this._clearImagePreviews()
  }

  // Submit-end hook (Turbo): reset + collapse on success.
  handleSuccess(event) {
    if (event?.detail?.success) {
      this.element.querySelector("form")?.reset()
      this._clearImagePreviews()
      this.collapse()
    }
  }

  // ─── Poll ────────────────────────────────────────────────────────────
  addPollOption(event) {
    event?.preventDefault()
    if (!this.hasPollOptionsTarget) return
    const rows = this.pollOptionsTarget.querySelectorAll(".cm-form-poll-row")
    if (rows.length >= 10) {
      alert("10 options au maximum.")
      return
    }
    const idx = rows.length

    const row = document.createElement("div")
    row.className = "cm-form-poll-row"

    const text = document.createElement("input")
    text.type = "text"
    text.name = `feed_post[poll_options_attributes][${idx}][text]`
    text.placeholder = `Option ${idx + 1}`
    text.className = "cm-form-input cm-form-poll-input"

    const pos = document.createElement("input")
    pos.type = "hidden"
    pos.name = `feed_post[poll_options_attributes][${idx}][position]`
    pos.value = String(idx)

    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "cm-form-poll-remove"
    btn.setAttribute("data-action", "click->cm-composer#removePollOption")
    btn.setAttribute("aria-label", "Retirer")
    btn.textContent = "×"

    row.appendChild(text)
    row.appendChild(pos)
    row.appendChild(btn)
    this.pollOptionsTarget.appendChild(row)
  }

  removePollOption(event) {
    event?.preventDefault()
    if (!this.hasPollOptionsTarget) return
    const rows = this.pollOptionsTarget.querySelectorAll(".cm-form-poll-row")
    if (rows.length <= 2) {
      alert("2 options au minimum.")
      return
    }
    event.currentTarget.closest(".cm-form-poll-row")?.remove()
  }

  // ─── Images ──────────────────────────────────────────────────────────
  previewImages(event) {
    if (!this.hasImagePreviewTarget) return
    const files = Array.from(event.target.files || [])
    this.imagePreviewTarget.replaceChildren()
    files.forEach((file) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = document.createElement("img")
        img.src = e.target.result
        img.className = "cm-form-image-thumb"
        this.imagePreviewTarget.appendChild(img)
      }
      reader.readAsDataURL(file)
    })
  }

  // ─── Internals ───────────────────────────────────────────────────────
  _show() {
    if (this.hasFormTarget) this.formTarget.hidden = false
    if (this.hasTriggerTarget) this.triggerTarget.hidden = true
  }

  _switchPanel(type) {
    const panels = {
      image: this.hasImagePanelTarget ? this.imagePanelTarget : null,
      link:  this.hasLinkPanelTarget  ? this.linkPanelTarget  : null,
      poll:  this.hasPollPanelTarget  ? this.pollPanelTarget  : null,
    }
    Object.entries(panels).forEach(([key, el]) => {
      if (el) el.hidden = (key !== type)
    })
    if (this.hasPostTypeTarget) {
      this.postTypeTarget.value = ["image", "link", "poll"].includes(type) ? type : "text"
    }
  }

  _clearImagePreviews() {
    if (this.hasImagePreviewTarget) this.imagePreviewTarget.replaceChildren()
  }
}
